import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

// PrimeReact imports
import { Panel } from 'primereact/panel';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { Toolbar } from 'primereact/toolbar';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

// Import des composants créés
import CardLifecycleManager from './CardLifecycleManager';
import CardStatusTracker from './CardStatusTracker';
import PrintingWithLifecycleManager from './Printing/PrintingWithLifecycleManager';
import TraceabilityReports from './Reports/TraceabilityReports';
import RealTimeAlerts from './Alerts/RealTimeAlerts';

// Import des NOUVEAUX composants
import ProductionStepManager from './ProductionStepManager';
import ProductionDashboard from './ProductionDashboard';
import BatchDetailsManager from './BatchDetailsManager';
import CardHistoryViewer from './CardHistoryViewer';
import AnomalyTracker from './AnomalyTracker';
import QualityControlPanel from './QualityControlPanel';
import DataExportPanel from './DataExportPanel';
import StockRestockPanel from './StockRestockPanel';
import BeneficiaryCardSearch from './BeneficiaryCardSearch';
import DuplicateCardManager from './DuplicateCardManager';
import PrintBatchManager from './PrintBatchManager';
import BatchCardsManager from './BatchCardsManager';
import PrintedCardsReport from './PrintedCardsReport';
import ComprehensiveReports from './ComprehensiveReports';
import StockMovements from './StockManagement/StockMovements';
import StockReports from './StockManagement/StockReports';

// Import des composants CarteV2
import TemplateManager from '../CarteV2/Templates/TemplateManager';
import GenerationIndividuelle from '../CarteV2/Generation/GenerationIndividuelle';
import GenerationBatch from '../CarteV2/Generation/GenerationBatch';
// import StockManagement from '../CarteV2/Stock/StockManagement'; // TODO: À créer
// import SystemValidation from '../CarteV2/Validation/SystemValidation'; // TODO: À créer
import BatchManagement from '../CarteV2/Production/BatchManagement';
// import DashboardAnalytics from '../CarteV2/Dashboard/DashboardAnalytics'; // TODO: À créer
// import CardLifecycle from '../CarteV2/Lifecycle/CardLifecycle'; // TODO: À créer
// import AdvancedSearch from '../CarteV2/Search/AdvancedSearch'; // TODO: À créer
// import PaymentManagement from '../CarteV2/Payment/PaymentManagement'; // TODO: À créer

// Import des services réels
import CardManagementService from '../../../services/CardManagementService';
import CarteV2Service from '../../../services/CarteV2Service';
import { stockService } from '../../../jsx/services/StockService';
import { paiementsService } from '../../../jsx/services/CartesPaiementsService';

const CardManagementCompletePage = () => {
    const { t } = useTranslation();
    const [activeIndex, setActiveIndex] = useState(0);
    const [cards, setCards] = useState([]);
    const [stockSummary, setStockSummary] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedCardForHistory, setSelectedCardForHistory] = useState(null);

    // Mapping des onglets vers les ancres
    const tabHashMap = {
        0: 'production',
        1: 'beneficiaries',
        2: 'stock'
    };

    const hashTabMap = {
        'production': 0,
        'beneficiaries': 1,
        'stock': 2
    };

    // Initialiser l'onglet actif en fonction de l'ancre dans l'URL
    useEffect(() => {
        const hash = window.location.hash.replace('#', '');
        if (hash && hashTabMap[hash] !== undefined) {
            setActiveIndex(hashTabMap[hash]);
        }
    }, []);

    // Gérer la navigation via les boutons précédent/suivant du navigateur
    useEffect(() => {
        const handlePopState = () => {
            const hash = window.location.hash.replace('#', '');
            if (hash && hashTabMap[hash] !== undefined) {
                setActiveIndex(hashTabMap[hash]);
            } else if (!hash) {
                setActiveIndex(0); // Retour à l'onglet par défaut
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setLoading(true);

        try {
            // Charger les données en parallèle
            const [cardsData, stockData] = await Promise.allSettled([
                CardManagementService.searchCards({
                    page: 1,
                    pageSize: 100,
                    sortBy: 'ProductionDate',
                    sortDirection: 'desc'
                }),
                stockService.getStockSummary()
            ]);

            // Traiter les résultats des cartes
            if (cardsData.status === 'fulfilled' && cardsData.value?.data) {
                setCards(cardsData.value.data || cardsData.value);
            } else if (cardsData.status === 'fulfilled') {
                setCards(cardsData.value || []);
            } else {
                console.warn(t('cardManagementComplete.messages.errorLoadingCards'), cardsData.reason);
                setCards([]);
            }

            // Traiter les données du stock
            if (stockData.status === 'fulfilled') {
                setStockSummary(stockData.value || []);
            } else {
                console.warn(t('cardManagementComplete.messages.errorLoadingStock'), stockData.reason);
                setStockSummary([]);
            }

        } catch (error) {
            console.error(t('cardManagementComplete.messages.errorInitialLoad'), error);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await loadInitialData();
        } finally {
            setRefreshing(false);
        }
    };

    const handleStatusChanged = async (cardId, newStatus) => {
        try {
            const updatedCards = await CardManagementService.searchCards({
                page: 1,
                pageSize: 100,
                sortBy: 'ProductionDate',
                sortDirection: 'desc'
            });

            if (updatedCards?.data) {
                setCards(updatedCards.data);
            } else {
                setCards(updatedCards || []);
            }
        } catch (error) {
            console.error(t('cardManagementComplete.messages.errorReloadingCards'), error);
        }
    };

    const handleSerialRangeCreated = async (newRange) => {
        console.log(t('cardManagementComplete.messages.newRangeCreated'), newRange);
        try {
            const updatedStock = await stockService.getStockSummary();
            setStockSummary(updatedStock || []);
        } catch (error) {
            console.error(t('cardManagementComplete.messages.errorReloadingStock'), error);
        }
    };

    // Gérer le changement d'onglet avec mise à jour de l'URL
    const handleTabChange = (e) => {
        const newIndex = e.index;
        setActiveIndex(newIndex);

        // Mettre à jour l'ancre dans l'URL
        const hash = tabHashMap[newIndex];
        if (hash) {
            window.history.pushState(null, '', `#${hash}`);
        }
    };

    // Fonctions pour les templates PrimeReact
    const renderCardsTable = () => {
        const statusBodyTemplate = (rowData) => {
            const status = CardManagementService.getStatusBadgeConfig(rowData.status);
            return <Tag value={status.text} severity={getSeverity(status.variant)} />;
        };

        const dateBodyTemplate = (rowData) => {
            return new Date(rowData.creationDate || rowData.lastStatusChange).toLocaleDateString('fr-FR');
        };

        const beneficiaryBodyTemplate = (rowData) => {
            return (
                <div>
                    <div className="font-bold">{rowData.beneficiaryName || `${t('cardManagementComplete.columns.beneficiary')} ${rowData.beneficiaryId}`}</div>
                    <div className="text-sm text-gray-500">ID: {rowData.beneficiaryId}</div>
                </div>
            );
        };

        return (
            <DataTable
                value={cards}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                loading={loading}
                className="p-datatable-sm"
                responsiveLayout="scroll"
                emptyMessage={t('cardManagementComplete.messages.noCardsFound')}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate={t('cardManagementComplete.messages.paginationCards', { first: '{first}', last: '{last}', totalRecords: '{totalRecords}' })}
            >
                <Column field="serialNumber" header={t('cardManagementComplete.columns.serialNumber')} sortable style={{ minWidth: '150px' }} />
                <Column field="beneficiaryId" header={t('cardManagementComplete.columns.beneficiary')} body={beneficiaryBodyTemplate} sortable style={{ minWidth: '200px' }} />
                <Column field="status" header={t('cardManagementComplete.columns.status')} body={statusBodyTemplate} sortable style={{ minWidth: '150px' }} />
                <Column field="creationDate" header={t('cardManagementComplete.columns.creationDate')} body={dateBodyTemplate} sortable style={{ minWidth: '150px' }} />
            </DataTable>
        );
    };

    const renderStockTable = () => {
        const statusBodyTemplate = (rowData) => {
            const status = stockService.formatStockStatus(rowData);
            return <Tag value={status.text} severity={getSeverity(status.variant)} />;
        };

        const quantityBodyTemplate = (rowData) => {
            return (
                <div className="text-center">
                    <div className="font-bold">{rowData.currentQuantity || 0}</div>
                    <div className="text-sm text-gray-500">{t('cardManagementComplete.columns.min')}: {rowData.minimumQuantity || 0}</div>
                </div>
            );
        };

        return (
            <DataTable
                value={stockSummary}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25]}
                loading={loading}
                className="p-datatable-sm"
                responsiveLayout="scroll"
                emptyMessage={t('cardManagementComplete.messages.noStockFound')}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate={t('cardManagementComplete.messages.paginationItems', { first: '{first}', last: '{last}', totalRecords: '{totalRecords}' })}
            >
                <Column field="stockType" header={t('cardManagementComplete.columns.stockType')} sortable style={{ minWidth: '150px' }} />
                <Column field="productCode" header={t('cardManagementComplete.columns.productCode')} sortable style={{ minWidth: '150px' }} />
                <Column field="currentQuantity" header={t('cardManagementComplete.columns.quantity')} body={quantityBodyTemplate} sortable style={{ minWidth: '120px' }} />
                <Column field="isActive" header={t('cardManagementComplete.columns.status')} body={statusBodyTemplate} sortable style={{ minWidth: '120px' }} />
            </DataTable>
        );
    };

    const getSeverity = (variant) => {
        switch (variant) {
            case 'success': return 'success';
            case 'warning': return 'warning';
            case 'danger': return 'danger';
            case 'info': return 'info';
            case 'primary': return 'info';
            default: return null;
        }
    };

    const rightToolbarTemplate = () => {
        return (
            <Button
                label={t('cardManagementComplete.buttons.refresh')}
                icon={refreshing ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
                className="p-button-outlined p-button-primary"
                onClick={handleRefresh}
                disabled={refreshing}
            />
        );
    };

    return (
        <div className="card-management-complete p-4">
            {/* En-tête principal */}
            <Panel className="mb-4">
                <div className="flex justify-content-between align-items-center">
                    <h5 className="m-0 text-900 font-semibold">{t('cardManagementComplete.title')}</h5>
                    {rightToolbarTemplate()}
                </div>
            </Panel>

            {/* Navigation par onglets avec PrimeReact */}
            <TabView
                activeIndex={activeIndex}
                onTabChange={handleTabChange}
                className="tabview-custom"
            >
                {/* PRODUCTION & GÉNÉRATION */}
                <TabPanel header={t('cardManagementComplete.tabs.production')} leftIcon="pi pi-cog">
                    <TabView>
                        <TabPanel header={t('cardManagementComplete.subTabs.productionDashboard')} leftIcon="pi pi-chart-bar">
                            <Panel header={t('cardManagementComplete.panels.productionDashboard')} className="mb-4">
                                <div className="mb-3">
                                    <small className="text-color-secondary">
                                        {t('cardManagementComplete.helperText.productionOverview')}
                                    </small>
                                </div>
                                <ProductionDashboard />
                            </Panel>
                        </TabPanel>
                        <TabPanel header={t('cardManagementComplete.subTabs.individualGeneration')} leftIcon="pi pi-user">
                            <GenerationIndividuelle />
                        </TabPanel>
                        <TabPanel header={t('cardManagementComplete.subTabs.batchGeneration')} leftIcon="pi pi-users">
                            <GenerationBatch />
                        </TabPanel>
                        <TabPanel header={t('cardManagementComplete.subTabs.templates')} leftIcon="pi pi-file">
                            <TemplateManager />
                        </TabPanel>
                        <TabPanel
                            header={t('cardManagementComplete.subTabs.unifiedBatchManagement')}
                            leftIcon="pi pi-th-large"
                        >
                            <Panel header={t('cardManagementComplete.panels.completeBatchManagement')} className="mb-4">
                                <div className="mb-3">
                                    <small className="text-color-secondary">
                                        {t('cardManagementComplete.helperText.unifiedModule')}
                                        <br />
                                        {t('cardManagementComplete.helperText.unifiedFeatures')}
                                    </small>
                                </div>
                                <BatchManagement />
                            </Panel>
                        </TabPanel>
                    </TabView>
                </TabPanel>
                {/* GESTION DES ASSURÉS */}
                <TabPanel header={t('cardManagementComplete.tabs.beneficiaries')} leftIcon="pi pi-users">
                    <TabView>
                        {/* <TabPanel header={t('cardManagementComplete.subTabs.searchBeneficiary')} leftIcon="pi pi-search">
                            <BeneficiaryCardSearch />
                        </TabPanel> */}
                        <TabPanel header={t('cardManagementComplete.subTabs.duplicateManagement')} leftIcon="pi pi-copy">
                            <DuplicateCardManager />
                        </TabPanel>
                        {/*<TabPanel header={t('cardManagementComplete.subTabs.cardHistory')} leftIcon="pi pi-history">
                            <Panel header={t('cardManagementComplete.panels.completeHistory')} className="mb-4">
                                <div className="mb-3">
                                    <small className="text-color-secondary">
                                        {t('cardManagementComplete.helperText.historyPaginated')}
                                    </small>
                                </div> */}

                                 {/* Sélection de carte 
                                <div className="mb-4">
                                    <label htmlFor="card-selector" className="font-semibold mb-2 block">
                                        {t('cardManagementComplete.labels.selectCard')}
                                    </label>
                                    <Dropdown
                                        id="card-selector"
                                        value={selectedCardForHistory}
                                        options={cards}
                                        onChange={(e) => setSelectedCardForHistory(e.value)}
                                        optionLabel="cardNumber"
                                        placeholder={t('cardManagementComplete.placeholders.chooseCard')}
                                        filter
                                        filterBy="cardNumber,beneficiaryName"
                                        emptyMessage={t('cardManagementComplete.messages.noCardsAvailable')}
                                        className="w-full md:w-20rem"
                                        itemTemplate={(option) => (
                                            <div className="flex align-items-center">
                                                <i className="pi pi-id-card mr-2"></i>
                                                <div>
                                                    <div className="font-semibold">{option.cardNumber || 'N/A'}</div>
                                                    <small className="text-color-secondary">{option.beneficiaryName || t('cardManagementComplete.messages.unknownBeneficiary')}</small>
                                                </div>
                                            </div>
                                        )}
                                        valueTemplate={(option) => {
                                            if (option) {
                                                return (
                                                    <div className="flex align-items-center">
                                                        <i className="pi pi-id-card mr-2"></i>
                                                        <span>{option.cardNumber}</span>
                                                    </div>
                                                );
                                            }
                                            return <span>{t('cardManagementComplete.placeholders.chooseCard')}</span>;
                                        }}
                                    />
                                </div>

                                {/* Affichage de l'historique /}
                                {selectedCardForHistory ? (
                                    <div className="mt-3">
                                        <div className="mb-3 p-3 bg-blue-50 border-round">
                                            <div className="flex align-items-center gap-3">
                                                <i className="pi pi-info-circle text-blue-500 text-2xl"></i>
                                                <div>
                                                    <div className="font-semibold text-900">{t('cardManagementComplete.labels.selectedCard')}: {selectedCardForHistory.cardNumber}</div>
                                                    <div className="text-sm text-600">{t('cardManagementComplete.columns.beneficiary')}: {selectedCardForHistory.beneficiaryName}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <CardHistoryViewer cardId={selectedCardForHistory.id} />
                                    </div>
                                ) : (
                                    <Message
                                        severity="info"
                                        text={t('cardManagementComplete.messages.selectCardForHistory')}
                                    />
                                )}
                            </Panel>
                        </TabPanel> */}
                    </TabView>
                </TabPanel>

                {/* STOCK & APPROVISIONNEMENT */}
                <TabPanel header={t('cardManagementComplete.tabs.stock')} leftIcon="pi pi-box">
                    <TabView>
                        <TabPanel header={t('cardManagementComplete.subTabs.stockInventory')} leftIcon="pi pi-list">
                            <Panel header={t('cardManagementComplete.panels.stockInventory')} className="mb-4">
                                <div className="mb-3">
                                    <small className="text-color-secondary">
                                        {t('cardManagementComplete.helperText.stockOverview')}
                                    </small>
                                </div>
                                {renderStockTable()}
                            </Panel>
                        </TabPanel>
                        <TabPanel header={t('cardManagementComplete.subTabs.stockMovements')} leftIcon="pi pi-arrows-h">
                            <StockMovements />
                        </TabPanel>
                        <TabPanel header={t('cardManagementComplete.subTabs.stockReports')} leftIcon="pi pi-chart-bar">
                            <StockReports />
                        </TabPanel>
                        <TabPanel header={t('cardManagementComplete.subTabs.restocking')} leftIcon="pi pi-shopping-cart">
                            <StockRestockPanel />
                        </TabPanel>
                    </TabView>
                </TabPanel>

                {/* QUALITÉ & CONTRÔLE */}
                {/* <TabPanel header={t('cardManagementComplete.tabs.quality')} leftIcon="pi pi-shield">
                    <TabView>
                        <TabPanel header={t('cardManagementComplete.subTabs.qualityControl')} leftIcon="pi pi-check-circle">
                            <Panel header={t('cardManagementComplete.panels.qualityControls')} className="mb-4">
                                <div className="mb-3">
                                    <small className="text-color-secondary">
                                        {t('cardManagementComplete.helperText.qualityControlInfo')}
                                    </small>
                                </div>
                                {cards.length > 0 ? (
                                    <QualityControlPanel cardId={cards[0]?.id} />
                                ) : (
                                    <Message severity="info" text={t('cardManagementComplete.messages.selectCardForQuality')} />
                                )}
                            </Panel>
                        </TabPanel>
                        <TabPanel header={t('cardManagementComplete.subTabs.anomalyDetection')} leftIcon="pi pi-exclamation-triangle">
                            <Panel header={t('cardManagementComplete.panels.anomalyTracking')} className="mb-4">
                                <div className="mb-3">
                                    <small className="text-color-secondary">
                                        {t('cardManagementComplete.helperText.anomalyInfo')}
                                    </small>
                                </div>
                                <AnomalyTracker />
                            </Panel>
                        </TabPanel>
                    </TabView>
                </TabPanel> */}

                {/* GESTION AVANCÉE & CYCLE DE VIE */}
                {/* <TabPanel header={t('cardManagementComplete.tabs.advanced')} leftIcon="pi pi-sliders-h">
                    <TabView>
                        <TabPanel header={t('cardManagementComplete.subTabs.lifecycle')} leftIcon="pi pi-sync">
                            <CardLifecycle />
                        </TabPanel>
                        <TabPanel header={t('cardManagementComplete.subTabs.advancedSearch')} leftIcon="pi pi-search-plus">
                            <AdvancedSearch />
                        </TabPanel>
                        <TabPanel header={t('cardManagementComplete.subTabs.systemValidation')} leftIcon="pi pi-check-circle">
                            <SystemValidation />
                        </TabPanel>
                    </TabView>
                </TabPanel> */}

                {/* PAIEMENTS */}
                {/* <TabPanel header={t('cardManagementComplete.tabs.payments')} leftIcon="pi pi-wallet">
                    <PaymentManagement />
                </TabPanel> */}

                {/* RAPPORTS & ANALYTICS */}
                {/* <TabPanel header={t('cardManagementComplete.tabs.reports')} leftIcon="pi pi-chart-line">
                    <Panel header={t('cardManagementComplete.panels.comprehensiveReports')} className="mb-4">
                        <div className="mb-3">
                            <small className="text-color-secondary">
                                {t('cardManagementComplete.helperText.reportsOverview')}
                                <br />
                                {t('cardManagementComplete.helperText.reportFeatures')}
                            </small>
                        </div>
                        <ComprehensiveReports />
                    </Panel>
                </TabPanel> */}

                {/* EXPORT DONNÉES */}
                {/* <TabPanel header={t('cardManagementComplete.tabs.dataExport')} leftIcon="pi pi-download">
                    <Panel header={t('cardManagementComplete.panels.exportReports')} className="mb-4">
                        <div className="mb-3">
                            <small className="text-color-secondary">
                                {t('cardManagementComplete.helperText.exportInfo')}
                            </small>
                        </div>
                        <DataExportPanel />
                    </Panel>
                </TabPanel> */}

                {/* CARTES IMPRIMÉES */}
                {/* <TabPanel header={t('cardManagementComplete.tabs.printedCards')} leftIcon="pi pi-print">
                    <Panel header={t('cardManagementComplete.panels.printedCardsReport')} className="mb-4">
                        <div className="mb-3">
                            <small className="text-color-secondary">
                                {t('cardManagementComplete.helperText.printedCardsInfo')}
                            </small>
                        </div>
                        <PrintedCardsReport />
                    </Panel>
                </TabPanel> */}
            </TabView>

            {/* CSS personnalisé pour corriger les problèmes de couleurs */}
            <style>{`
                .card-management-complete {
                    background-color: #f8f9fa;
                    min-height: 100vh;
                }

                .tabview-custom .p-tabview-nav {
                    background: white;
                    border-bottom: 1px solid #dee2e6;
                }

                .tabview-custom .p-tabview-nav li .p-tabview-nav-link {
                    color: #495057 !important;
                    background: transparent !important;
                    border-color: transparent !important;
                }

                .tabview-custom .p-tabview-nav li.p-highlight .p-tabview-nav-link {
                    color: #007bff !important;
                    border-bottom-color: #007bff !important;
                }

                .tabview-custom .p-tabview-nav li .p-tabview-nav-link:hover {
                    color: #007bff !important;
                }

                .p-panel .p-panel-header {
                    background: #fff !important;
                    color: #495057 !important;
                    border-bottom: 1px solid #dee2e6 !important;
                }

                .p-panel .p-panel-content {
                    background: #fff !important;
                    color: #495057 !important;
                }

                .p-datatable .p-datatable-header {
                    background: #f8f9fa !important;
                    color: #495057 !important;
                }

                .p-datatable .p-datatable-thead > tr > th {
                    background: #f8f9fa !important;
                    color: #495057 !important;
                    border-color: #dee2e6 !important;
                }

                .p-datatable .p-datatable-tbody > tr > td {
                    background: white !important;
                    color: #495057 !important;
                    border-color: #dee2e6 !important;
                }

                .p-datatable .p-datatable-tbody > tr:nth-child(even) > td {
                    background: #f8f9fa !important;
                }

                .p-button.p-button-outlined {
                    color: #007bff !important;
                    border-color: #007bff !important;
                }

                .text-color-secondary {
                    color: #6c757d !important;
                }

                .text-primary {
                    color: #007bff !important;
                }
            `}</style>
        </div>
    );
};

export default CardManagementCompletePage;